import { CommissionTrackerHeaders, MatchActions } from '../types/index.js';
import { downloadCSV, createHubSpotDealLink, getMonthYear } from '../utils/index.js';

/**
 * Exports match results as Commission Tracker CSV
 * @param {Array} matchResults - Array of match results
 * @param {string} filename - Optional filename for export
 */
export function exportCommissionTracker(matchResults, filename = 'Commission_Tracker.csv') {
  if (!matchResults || !Array.isArray(matchResults)) {
    throw new Error('No match results to export');
  }

  // Filter for commission-ready records only
  const readyRecords = matchResults.filter(result => 
    result.action === MatchActions.READY && 
    result.hubspotRecord && 
    result.wellskyRecord
  );

  if (readyRecords.length === 0) {
    throw new Error('No commission-ready records found to export');
  }

  // Transform to commission tracker format
  const commissionData = readyRecords.map(result => {
    const hubspot = result.hubspotRecord;
    const wellsky = result.wellskyRecord;
    
    return {
      [CommissionTrackerHeaders.HUBSPOT_DEAL_LINK]: createHubSpotDealLink(hubspot.recordId),
      [CommissionTrackerHeaders.DATABASE_ID]: wellsky.databaseId,
      [CommissionTrackerHeaders.WELLSKY_FULL_NAME]: wellsky.fullName,
      [CommissionTrackerHeaders.HUBSPOT_FULL_NAME]: hubspot.fullName,
      [CommissionTrackerHeaders.HUBSPOT_CLIENT_NAME]: hubspot.clientName,
      [CommissionTrackerHeaders.AGENCY_SUBDOMAIN]: wellsky.agencySubdomain,
      [CommissionTrackerHeaders.START_DATE]: wellsky.startDate,
      [CommissionTrackerHeaders.CLOSE_DATE]: hubspot.closeDate,
      [CommissionTrackerHeaders.INSIDE_SALES_REP]: hubspot.salesRep,
      [CommissionTrackerHeaders.REP_COMMISSION]: '', // To be calculated
      [CommissionTrackerHeaders.MANAGER_COMMISSION]: '', // To be calculated
      [CommissionTrackerHeaders.MONTH_YEAR]: getMonthYear(hubspot.closeDate),
      [CommissionTrackerHeaders.TOTAL_DEALS_WON]: '', // To be calculated
      [CommissionTrackerHeaders.TOTAL_COMMISSION]: '', // To be calculated
      [CommissionTrackerHeaders.CHARITY_GRANDCHAMPT_MONTHLY_DEALS_WON]: '',
      [CommissionTrackerHeaders.CHARITY_GRANDCHAMPT_MONTHLY_COMMISSION]: '',
      [CommissionTrackerHeaders.IRVIN_TRAHAN_III_MONTHLY_DEALS_WON]: '',
      [CommissionTrackerHeaders.IRVIN_TRAHAN_III_MONTHLY_COMMISSION]: '',
      [CommissionTrackerHeaders.JOEL_THOMAS_MONTHLY_DEALS_WON]: '',
      [CommissionTrackerHeaders.JOEL_THOMAS_MONTHLY_COMMISSION]: '',
      [CommissionTrackerHeaders.TYSON_QUEEN_MONTHLY_DEALS_WON]: '',
      [CommissionTrackerHeaders.TYSON_QUEEN_MONTHLY_COMMISSION]: '',
      [CommissionTrackerHeaders.TOTAL_MANAGER_COMMISSION]: ''
    };
  });

  // Calculate commission data
  const commissionDataWithCalcs = calculateCommissions(commissionData);

  // Define headers for CSV export
  const headers = Object.keys(CommissionTrackerHeaders).map(key => ({
    key: CommissionTrackerHeaders[key],
    label: CommissionTrackerHeaders[key]
  }));

  // Download CSV
  downloadCSV(commissionDataWithCalcs, filename, headers);
  
  return {
    recordCount: commissionDataWithCalcs.length,
    filename
  };
}

/**
 * Calculates commission amounts for each record
 * @param {Array} commissionData - Array of commission records
 * @returns {Array} - Array with calculated commission values
 */
function calculateCommissions(commissionData) {
  // Commission rates (these could be configurable)
  const REP_COMMISSION_RATE = 0.05; // 5%
  const MANAGER_COMMISSION_RATE = 0.02; // 2%
  
  // Base commission amount (this could be dynamic based on deal value)
  const BASE_COMMISSION = 1000;

  // Group by sales rep and month/year for calculations
  const repStats = {};
  const monthlyStats = {};

  // First pass: calculate basic commissions and gather stats
  const processedData = commissionData.map(record => {
    const salesRep = record[CommissionTrackerHeaders.INSIDE_SALES_REP];
    const monthYear = record[CommissionTrackerHeaders.MONTH_YEAR];
    
    // Calculate commissions
    const repCommission = BASE_COMMISSION * REP_COMMISSION_RATE;
    const managerCommission = BASE_COMMISSION * MANAGER_COMMISSION_RATE;

    // Update rep stats
    if (!repStats[salesRep]) {
      repStats[salesRep] = { deals: 0, commission: 0 };
    }
    repStats[salesRep].deals += 1;
    repStats[salesRep].commission += repCommission;

    // Update monthly stats
    const monthlyKey = `${salesRep}-${monthYear}`;
    if (!monthlyStats[monthlyKey]) {
      monthlyStats[monthlyKey] = { deals: 0, commission: 0, rep: salesRep, month: monthYear };
    }
    monthlyStats[monthlyKey].deals += 1;
    monthlyStats[monthlyKey].commission += repCommission;

    return {
      ...record,
      [CommissionTrackerHeaders.REP_COMMISSION]: repCommission,
      [CommissionTrackerHeaders.MANAGER_COMMISSION]: managerCommission
    };
  });

  // Second pass: add aggregate data
  return processedData.map(record => {
    const salesRep = record[CommissionTrackerHeaders.INSIDE_SALES_REP];
    const monthYear = record[CommissionTrackerHeaders.MONTH_YEAR];

    // Total deals and commission for the rep
    const totalDeals = repStats[salesRep]?.deals || 0;
    const totalCommission = repStats[salesRep]?.commission || 0;

    // Rep-specific monthly data
    const getRepMonthlyData = (repName) => {
      const key = `${repName}-${monthYear}`;
      return monthlyStats[key] || { deals: 0, commission: 0 };
    };

    const charityData = getRepMonthlyData('Charity Grandchampt');
    const irvinData = getRepMonthlyData('Irvin Trahan III');
    const joelData = getRepMonthlyData('Joel Thomas');
    const tysonData = getRepMonthlyData('Tyson Queen');

    // Calculate total manager commission
    const totalManagerCommission = Object.values(repStats)
      .reduce((sum, stat) => sum + (stat.commission * MANAGER_COMMISSION_RATE), 0);

    return {
      ...record,
      [CommissionTrackerHeaders.TOTAL_DEALS_WON]: totalDeals,
      [CommissionTrackerHeaders.TOTAL_COMMISSION]: totalCommission,
      [CommissionTrackerHeaders.CHARITY_GRANDCHAMPT_MONTHLY_DEALS_WON]: charityData.deals,
      [CommissionTrackerHeaders.CHARITY_GRANDCHAMPT_MONTHLY_COMMISSION]: charityData.commission,
      [CommissionTrackerHeaders.IRVIN_TRAHAN_III_MONTHLY_DEALS_WON]: irvinData.deals,
      [CommissionTrackerHeaders.IRVIN_TRAHAN_III_MONTHLY_COMMISSION]: irvinData.commission,
      [CommissionTrackerHeaders.JOEL_THOMAS_MONTHLY_DEALS_WON]: joelData.deals,
      [CommissionTrackerHeaders.JOEL_THOMAS_MONTHLY_COMMISSION]: joelData.commission,
      [CommissionTrackerHeaders.TYSON_QUEEN_MONTHLY_DEALS_WON]: tysonData.deals,
      [CommissionTrackerHeaders.TYSON_QUEEN_MONTHLY_COMMISSION]: tysonData.commission,
      [CommissionTrackerHeaders.TOTAL_MANAGER_COMMISSION]: totalManagerCommission
    };
  });
}

/**
 * Exports match results as Data Match CSV
 * @param {Array} matchResults - Array of match results
 * @param {string} filename - Optional filename for export
 */
export function exportDataMatch(matchResults, filename = 'Data_Match.csv') {
  if (!matchResults || !Array.isArray(matchResults)) {
    throw new Error('No match results to export');
  }

  // Transform to data match format
  const dataMatchRecords = matchResults.map((result) => {
    const hubspot = result.hubspotRecord;
    const wellsky = result.wellskyRecord;
    
    return {
      'Hubspot Row': result.hubspotIndex + 1,
      'Wellsky Row': result.wellskyIndex !== null ? result.wellskyIndex + 1 : '',
      'Action Required': result.action,
      'Investigation Reason': result.investigationReason || '',
      'Commission Eligible': hubspot?.commissionEligible ? 'Yes' : 'No',
      'Hubspot Deal Link': hubspot ? createHubSpotDealLink(hubspot.recordId) : '',
      'Database ID': wellsky?.databaseId || hubspot?.wellskyDatabaseId || '',
      'Wellsky Full Name': wellsky?.fullName || '',
      'Hubspot Full Name': hubspot?.fullName || '',
      'Hubspot Client Name': hubspot?.clientName || '',
      'Agency Subdomain': wellsky?.agencySubdomain || '',
      'Start Date': wellsky?.startDate || '',
      'Close Date': hubspot?.closeDate || '',
      'Inside Sales Rep': hubspot?.salesRep || '',
      'Database ID Match': result.criteria?.idMatch || (result.matchedCriterion && result.matchedCriterion.includes('Database ID')) ? 'Yes' : 'No',
      'Full Name Match': result.criteria?.nameMatch || (result.matchedCriterion && result.matchedCriterion.includes('Name')) ? 'Yes' : 'No',
      'Phone Number Match': result.criteria?.phoneMatch || (result.matchedCriterion && result.matchedCriterion.includes('Phone')) ? 'Yes' : 'No',
      'Email Match': result.criteria?.emailMatch || (result.matchedCriterion && result.matchedCriterion.includes('Email')) ? 'Yes' : 'No',
      'Street Number + Postal Code Match': result.criteria?.addressMatch || (result.matchedCriterion && result.matchedCriterion.includes('Address')) ? 'Yes' : 'No',
      'Match Count': result.score
    };
  });

  // Define headers for CSV export
  const headers = [
    { key: 'Hubspot Row', label: 'Hubspot Row' },
    { key: 'Wellsky Row', label: 'Wellsky Row' },
    { key: 'Action Required', label: 'Action Required' },
    { key: 'Investigation Reason', label: 'Investigation Reason' },
    { key: 'Commission Eligible', label: 'Commission Eligible' },
    { key: 'Hubspot Deal Link', label: 'Hubspot Deal Link' },
    { key: 'Database ID', label: 'Database ID' },
    { key: 'Wellsky Full Name', label: 'Wellsky Full Name' },
    { key: 'Hubspot Full Name', label: 'Hubspot Full Name' },
    { key: 'Hubspot Client Name', label: 'Hubspot Client Name' },
    { key: 'Agency Subdomain', label: 'Agency Subdomain' },
    { key: 'Start Date', label: 'Start Date' },
    { key: 'Close Date', label: 'Close Date' },
    { key: 'Inside Sales Rep', label: 'Inside Sales Rep' },
    { key: 'Database ID Match', label: 'Database ID Match' },
    { key: 'Full Name Match', label: 'Full Name Match' },
    { key: 'Phone Number Match', label: 'Phone Number Match' },
    { key: 'Email Match', label: 'Email Match' },
    { key: 'Street Number + Postal Code Match', label: 'Street Number + Postal Code Match' },
    { key: 'Match Count', label: 'Match Count' }
  ];

  // Download CSV
  downloadCSV(dataMatchRecords, filename, headers);
  
  return {
    recordCount: dataMatchRecords.length,
    filename
  };
}

/**
 * Exports formatted data as CSV
 * @param {Array} data - Array of formatted records
 * @param {string} type - Type of data ('wellsky' or 'hubspot')
 * @param {string} filename - Optional filename for export
 */
export function exportFormattedData(data, type, filename = null) {
  if (!data || !Array.isArray(data)) {
    throw new Error('No data to export');
  }

  const defaultFilename = type === 'wellsky' ? 'Wellsky_Formatted_Data.csv' : 'Hubspot_Formatted_Data.csv';
  const exportFilename = filename || defaultFilename;

  // For formatted data, export all properties
  const headers = data.length > 0 ? Object.keys(data[0])
    .filter(key => !key.startsWith('_')) // Exclude internal properties
    .map(key => ({ key, label: key })) : [];

  downloadCSV(data, exportFilename, headers);
  
  return {
    recordCount: data.length,
    filename: exportFilename
  };
}

/**
 * Gets export statistics
 * @param {Array} matchResults - Array of match results
 * @returns {object} - Export statistics
 */
export function getExportStats(matchResults) {
  if (!matchResults || !Array.isArray(matchResults)) {
    return {
      total: 0,
      commissionReady: 0,
      needsInvestigation: 0,
      notEligible: 0
    };
  }

  const stats = {
    total: matchResults.length,
    commissionReady: 0,
    needsInvestigation: 0,
    notEligible: 0
  };

  matchResults.forEach(result => {
    switch (result.action) {
      case MatchActions.READY:
        stats.commissionReady++;
        break;
      case MatchActions.INVESTIGATION:
        stats.needsInvestigation++;
        break;
      case MatchActions.NOT_ELIGIBLE:
        stats.notEligible++;
        break;
      default:
        break;
    }
  });

  return stats;
}
